import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormikInputField from 'commons/components/FormFields/Formik/FormikInputField';
import ItemSelectField from 'commons/components/FormFields/ItemSelectField';
import ConfigurationContext from 'UserRegistration/contexts/ConfigurationContext';
import DataContext from 'UserRegistration/contexts/DataContext';
import { isSaveButtonDisabled } from 'UserRegistration/contexts/configs/componentConfig';
import { saveSection } from 'UserRegistration/resources/utils/saveUtils';
import { validateSingleField, formatBackendErrors, resetErrorForField, containsErrorType, getContactIdForErrorType } from 'UserRegistration/resources/utils/ValidateUtils';
import { validateUsername, validateEmail, validateUnifiedUserEmailFormat } from 'UserRegistration/resources/api/validation';
import RegistrationSection from 'UserRegistration/components/RegistrationSteps/RegistrationSection';
import { updatePersonalInformationContextValue, populateUsername, updateContextValuesOnChange } from 'UserRegistration/resources/utils/personalInfoUtils';
import MetsMessages, {MetsMessagesSeverityEnum} from "../../../../commons/components/MetsMessages";

function AddContactPersonalInformation(props) {

    const { componentConfig } = useContext(ConfigurationContext);
    const { registrationData, setRegistrationData } = useContext(DataContext);
    const [ getShowUniqueUsernameErrorMessage, setShowUniqueUsernameErrorMessage ] = useState(false);
    const [ contactId, setContactId ] = useState(null)
    const { t } = useTranslation(componentConfig.i18nNamespaces);

    useEffect(() => {

        updatePersonalInformationContextValue("organizationName", props.organizationName, registrationData, setRegistrationData);

    }, []);

    const formikInitialValues = {
        userFirstName: "",
        userLastName: "",
        phoneNumber: "",
        timezone: "",
        emailAddress: "",
        username: ""
    };

    let yupValidationSchema = Yup.object({
        userFirstName: Yup.string()
            .trim()
            .required(t('error.required')),
        userLastName: Yup.string()
            .trim()
            .required(t('error.required')),
        phoneNumber: Yup.string()
            .required(t('error.required'))
            .test(
                'Phone Number Not Blank',
                t('error.required'),
                (value) => value && value.trim().length !== 0
            )
            .matches('^[-0-9+(). ]*$', t('error.phoneNumber.invalid.characters')),
        timezone: Yup.string()
            .required(t('error.required'))
    });

    if (componentConfig.supplierEmailAsUsername) {
        /**
         * MERX requires this call to the "validateUnifiedUserEmailFormat" function to validate the email format for 
         * the combined email/username field. For SSC and other portals that will have this combined email/username,
         * the email format will technically be validated twice: once with "validateUnifiedUserEmailFormat"
         * and once with "validateUsername". Eventually this double validation should be removed once MERX
         * switches to use the same backend validator as SSC.
         */
        yupValidationSchema = yupValidationSchema.shape({
            emailUsername: Yup.string()
                .required(t('error.required'))
                .test('Backend Email Validation', "Backend Error", function (value) {
                    setShowUniqueUsernameErrorMessage(false);
                    if (value) {
                        return validateUnifiedUserEmailFormat(value)
                            .then((data) => {
                                if (data && data.length !== 0) {
                                    let errorString = formatBackendErrors(data);
                                    return (this.createError({message: errorString}));
                                }
                                return true;
                            }).catch((e) => {
                                console.log(e);
                            });
                    }
                    return true;
                })
                .test('Backend Validation', "Backend Error", function (value) {
                    setShowUniqueUsernameErrorMessage(false);
                    if (value) {
                        return validateUsername(value)
                            .then((data) => {
                                if (data && data.length !== 0) {
                                    setShowUniqueUsernameErrorMessage(containsErrorType(data, "USERNAME_UNIQUE"));
                                    setContactId(getContactIdForErrorType(data, "USERNAME_UNIQUE"));
                                    let errorString = formatBackendErrors(data);
                                    return (this.createError({message: errorString}));
                                }
                                return true;
                            }).catch((e) => {
                                console.log(e);
                            });
                    }
                    return true;
                })
        });
    } else {
        yupValidationSchema = yupValidationSchema.shape({
            emailAddress: Yup.string()
                .required(t('error.required'))
                .email(t('error.email.invalid'))
                .test('Backend Validation', 'Backend Error', function (value) {

                    if (value) {

                        return validateEmail(value).then((data) => {

                            if (data) {
                                if (data.length !== 0) {
                                    let errorString = formatBackendErrors(data);
                                    return (this.createError({message: errorString}));
                                } else if (!registrationData.registrationModel.personalInformation.username) {
                                    populateUsername(value, props.formikRef, registrationData, setRegistrationData);
                                }
                                return true;
                            }

                        });

                    }

                    return true;

                }),
            username: Yup.string()
                .required(t('error.required'))
                .test('Backend Validation', "Backend Error", function (value) {
                    setShowUniqueUsernameErrorMessage(false);
                    if (value) {
                        return validateUsername(value)
                            .then((data) => {
                                if (data && data.length !== 0) {
                                    setShowUniqueUsernameErrorMessage(containsErrorType(data, "USERNAME_UNIQUE"));
                                    setContactId(getContactIdForErrorType(data, "USERNAME_UNIQUE"));
                                    let errorString = formatBackendErrors(data);
                                    return (this.createError({message: errorString}));
                                }
                                return true;
                            }).catch((e) => {
                                console.log(e);
                            });
                    }
                    return true;
                })
        });
    }
    

    const formikOnSubmit = () => {
        saveSection(componentConfig, registrationData, setRegistrationData);
    };

    return (

        <Formik
            initialValues={formikInitialValues}
            validationSchema={yupValidationSchema}
            onSubmit={formikOnSubmit}
            innerRef={props.formikRef}
            validateOnChange={false}
            validateOnBlur={false}>

            {formikProps => (

                <Form>

                    <RegistrationSection 
                        className="personal-info-section"
                        id="personalInfoSection"
                        saveButtonDisabled={isSaveButtonDisabled(registrationData, componentConfig)}
                        saveButtonOnClick={formikProps.submitForm}
                        sectionNumber={props.sectionNumber}
                        title={t('personalInformation.addContact')}>

                        <div className="section-fields">
                            <Field component={FormikInputField} label={t('firstName')} name="userFirstName" onChange={(e) => updateContextValuesOnChange(e, registrationData, setRegistrationData)} autoComplete="new-password" maxLength={50}
                                onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)} />
                            
                            <Field component={FormikInputField} label={t('lastName')} name="userLastName" onChange={(e) => updateContextValuesOnChange(e, registrationData, setRegistrationData)} autoComplete="new-password" maxLength={50}
                                onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)} />
                            
                            <Field component={FormikInputField} label={t('phoneNumber')} name="phoneNumber" onChange={(e) => updateContextValuesOnChange(e, registrationData, setRegistrationData)} autoComplete="new-password" maxLength={20}
                                onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)} />

                            <ItemSelectField 
                                error={formikProps.errors['timezone']}
                                items={props.supportedTimezones.map((timezone) => {
                                    return { label: timezone.resolvedMessage, value: timezone.value };
                                })}
                                label={t('timezone')}
                                name="timezone"
                                onBlur={() => validateSingleField("timezone", props.formikRef.current)}
                                onChange={(selectedItem) => {
                                    formikProps.setFieldValue('timezone', selectedItem.value, false);
                                    resetErrorForField('timezone', props.formikRef.current);
                                    updatePersonalInformationContextValue("timezone", selectedItem.value, registrationData, setRegistrationData)
                                }}
                                placeholder={t("chooseTimezone")}
                                touched={formikProps.touched['timezone']}
                            />
                        </div>

                        <h3 className="sub-section-title">
                            {t('connection.details')}
                        </h3>

                        <div className="section-fields">
                            {componentConfig.supplierEmailAsUsername
                                ?
                                <Field component={FormikInputField} label={t('emailUsername')} name="emailUsername"
                                       rowClassName="large-field"
                                       onChange={(e) => updateContextValuesOnChange(e, registrationData, setRegistrationData)}
                                       autoComplete="new-password" maxLength={40}
                                       onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)}/>
                                : <>
                                    <Field component={FormikInputField} label={t('emailAddress')} name="emailAddress"
                                           onChange={(e) => updateContextValuesOnChange(e, registrationData, setRegistrationData)}
                                           autoComplete="new-password" maxLength={40}
                                           onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)}/>

                                    <Field component={FormikInputField} label={t('username')} name="username"
                                           onChange={(e) => updateContextValuesOnChange(e, registrationData, setRegistrationData)}
                                           autoComplete="new-password" maxLength={40}
                                           onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)}/>
                                </>
                            }
                            {getShowUniqueUsernameErrorMessage ?
                                <MetsMessages severity={MetsMessagesSeverityEnum.Info} containerClassName="unavailable-username-info-container">
                                    {t('addContact.error.username.login.1')}
                                    <ul>
                                        {contactId &&
                                            <li><a href={`/private/supplier/contacts/${contactId}`}>{t('addContact.error.username.view.profile')}</a></li>
                                        }
                                        <li>{t('error.username.login.2', {phoneNumber: componentConfig.supportPhonenumber})}</li>
                                    </ul>
                                </MetsMessages>
                            : ''}

                        </div>

                    </RegistrationSection>

                </Form>

            )}

        </Formik> 
            
    );

}

AddContactPersonalInformation.propTypes = {
    /** A reference to the Formik component. */
    formikRef: PropTypes.any,
    /** The total number of configurable sections currently loaded in the instance of UserRegistration. */
    sectionCount: PropTypes.number,
    /** The position of this section in relation to other sections in the entire instance of UserRegistration. */
    sectionNumber: PropTypes.number,
    /** Organization name of the current logged in user. */
    organizationName: PropTypes.string,
    /** List of available timezones for the current portal. */
    supportedTimezones: PropTypes.array
}

AddContactPersonalInformation.defaultProps = {
    supportedTimezones: []
};

export default AddContactPersonalInformation;